import Button from "@/components/Button.vue";
import ConfirmationAlert from "@/components/ConfirmationAlert/ConfirmationAlert.vue";
import Container from "@/components/Container.vue";
import EmptyState from "@/components/EmptyState.vue";
import FormButton from "@/components/FormComponents/FormButton.vue";
import PasswordInput from "@/components/FormComponents/PasswordInput.vue";
import SelectInput from "@/components/FormComponents/SelectInput.vue";
import TextAreaInput from "@/components/FormComponents/TextAreaInput.vue";
import TextInput from "@/components/FormComponents/TextInput.vue";
import Header from "@/components/Header.vue";
import LoadingContainer from "@/components/LoadingContainer.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import Navbar from "@/components/Navbar.vue";
import Sidebar from "@/components/Sidebar.vue";
import { EventBus } from "@/utils/EventBus";
import "@popperjs/core";
import { VueQueryPlugin } from "@tanstack/vue-query";
import "bootstrap";
import "jquery";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import money from "v-money3";
import { createApp } from "vue";
import pkg from "./../package.json";
import App from "./App.vue";
import AvatarComponent from "./components/AvatarComponent.vue";
import AvatarUploader from "./components/AvatarUploader/AvatarUploader.vue";
import FileUploader from "./components/FileUploader/FileUploader.vue";
import CheckboxInput from "./components/FormComponents/CheckboxInput.vue";
import MoneyInput from "./components/FormComponents/MoneyInput.vue";
import RadioInput from "./components/FormComponents/RadioInput.vue";
import SwitchInput from "./components/FormComponents/SwitchInput.vue";
import ImageCropper from "./components/ImageCropper/ImageCropper.vue";
import ImageUploader from "./components/ImageUploader/ImageUploader.vue";
import OffcanvasComponent from "./components/OffcanvasComponent.vue";
import ScrollHorizontal from "./components/ScrollHorizontal.vue";
import TimezoneAlert from "./components/TimezoneAlert.vue";
import VotingStars from "./components/VotingStars.vue";
import YoutubeInput from "./components/YoutubeInput/YoutubeInput.vue";
import router from "./router";
import paths from "./router/paths";
import { useAuthTokenStore } from "./store/useAuthTokenStore";
import { useSessionStore } from "./store/useSessionStore";
import "./utils/Prototypes/prototypeFunctions";

// @ts-ignore
import "@fortawesome/fontawesome-free/css/all.css";
// @ts-ignore
import "moment/locale/pt-br";
// @ts-ignore
import "./assets/css/styles.scss";
// @ts-ignore
import "./assets/theme/scss/theme.scss";

const app = createApp(App);

app.config.globalProperties.$envString = `${pkg.version} ${process.env.VUE_APP_ENV_NAME}`;
app.config.globalProperties.$eventBus = EventBus;
app.config.globalProperties.$paths = paths;
app.config.globalProperties.$router = router;

app.component("Sidebar", Sidebar);
app.component("Navbar", Navbar);
app.component("LoadingContainer", LoadingContainer);
app.component("EmptyState", EmptyState);
app.component("Container", Container);
app.component("TextInput", TextInput);
app.component("SelectInput", SelectInput);
app.component("TextAreaInput", TextAreaInput);
app.component("PasswordInput", PasswordInput);
app.component("FormButton", FormButton);
app.component("ModalComponent", ModalComponent);
app.component("Header", Header);
app.component("ConfirmationAlert", ConfirmationAlert);
app.component("ImageCropper", ImageCropper);
app.component("RadioInput", RadioInput);
app.component("MoneyInput", MoneyInput);
app.component("CheckboxInput", CheckboxInput);
app.component("FileUploader", FileUploader);
app.component("Button", Button);
app.component("ScrollHorizontal", ScrollHorizontal);
app.component("OffcanvasComponent", OffcanvasComponent);
app.component("SwitchInput", SwitchInput);
app.component("AvatarUploader", AvatarUploader);
app.component("YoutubeInput", YoutubeInput);
app.component("ImageUploader", ImageUploader);
app.component("VotingStars", VotingStars);
app.component("AvatarComponent", AvatarComponent);
app.component("TimezoneAlert", TimezoneAlert);
const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(VueQueryPlugin).use(pinia).use(money);

// One-time migration from the legacy single-blob main store (pre-decomposition
// into per-concern Pinia stores). Only fills in session/token if the new,
// independently-persisted stores don't already have their own data. The
// legacy key is removed right after so this only ever runs once per browser —
// otherwise a stale legacy blob would keep resurrecting a cleared session on
// every subsequent boot (e.g. after logout).
const legacyStorageKey = process.env.VUE_APP_STORAGE_KEY as string;
const legacyState = JSON.parse(localStorage.getItem(legacyStorageKey) || "{}");
const sessionStore = useSessionStore(pinia);
const authTokenStore = useAuthTokenStore(pinia);
if (!sessionStore.session && legacyState.session) {
  sessionStore.save(legacyState.session);
}
if (!authTokenStore.token && legacyState.token) {
  authTokenStore.save(legacyState.token);
}
localStorage.removeItem(legacyStorageKey);

app.use(router).mount("#app");
